import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input, Button } from 'antd';
import { SendOutlined, RobotOutlined, DeleteOutlined } from '@ant-design/icons';
import { ChatMessage, ChatResponse } from '../../types/aiChat';
import {
  useAskChatMutation,
  useConfirmAiActionMutation,
  useCancelAiActionMutation,
} from '../../services/aiChatApi';
import { streamChat } from '../../services/aiChatStream';
import { useAppSelector } from '../../hooks/redux';
import AiChatMessage from './AiChatMessage';

const STORAGE_KEY = 'ai-chat-history';
const MAX_STORED_MESSAGES = 50;

function loadMessages(restaurantId: string): ChatMessage[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}-${restaurantId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((msg: ChatMessage) => {
      // A card left 'pending' from a previous session is stale — the staged
      // action lives in server memory with a short TTL, so mark it expired
      // instead of offering a Confirmar button that would fail. Same story
      // for a message that was mid-stream when the tab closed.
      const cleaned: ChatMessage = { ...msg, isStreaming: undefined };
      return cleaned.pendingActionStatus === 'pending'
        ? { ...cleaned, pendingActionStatus: 'expired' as const }
        : cleaned;
    });
  } catch {
    return [];
  }
}

function saveMessages(restaurantId: string, messages: ChatMessage[]) {
  try {
    const toStore = messages.slice(-MAX_STORED_MESSAGES);
    localStorage.setItem(`${STORAGE_KEY}-${restaurantId}`, JSON.stringify(toStore));
  } catch {
    // localStorage full or unavailable - silently ignore
  }
}

const AiChatPanel: React.FC = () => {
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const accessToken = useAppSelector((state) => (state as any).auth?.accessToken as string | undefined);
  const restaurantId = currentRestaurant?.id ?? '';

  const [messages, setMessages] = useState<ChatMessage[]>(() => loadMessages(restaurantId));
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [askChat] = useAskChatMutation();
  const [confirmAiAction, { isLoading: isConfirming }] = useConfirmAiActionMutation();
  const [cancelAiAction, { isLoading: isCancelling }] = useCancelAiActionMutation();
  const actionBusy = isConfirming || isCancelling;

  const lastMessage = messages[messages.length - 1];
  const isStreamingVisible = lastMessage?.isStreaming === true && lastMessage.content.length > 0;

  // Persist messages when they change
  useEffect(() => {
    if (restaurantId) {
      saveMessages(restaurantId, messages);
    }
  }, [messages, restaurantId]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, sending, scrollToBottom]);

  const handleClearChat = () => {
    setMessages([]);
    setError(null);
  };

  const setActionStatus = (actionId: string, status: ChatMessage['pendingActionStatus']) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.pendingAction?.id === actionId ? { ...msg, pendingActionStatus: status } : msg
      )
    );
  };

  const handleConfirmAction = async (actionId: string) => {
    if (!currentRestaurant || actionBusy) return;
    setError(null);
    try {
      const result = await confirmAiAction({
        restaurantId: currentRestaurant.id,
        actionId,
      }).unwrap();
      setActionStatus(actionId, result.success ? 'confirmed' : 'failed');
      setMessages((prev) => [...prev, { role: 'assistant', content: result.response }]);
    } catch {
      setError('Error al confirmar la accion. Intenta de nuevo.');
    }
  };

  const handleCancelAction = async (actionId: string) => {
    if (!currentRestaurant || actionBusy) return;
    setError(null);
    try {
      const result = await cancelAiAction({
        restaurantId: currentRestaurant.id,
        actionId,
      }).unwrap();
      setActionStatus(actionId, 'cancelled');
      setMessages((prev) => [...prev, { role: 'assistant', content: result.response }]);
    } catch {
      setError('Error al cancelar la accion. Intenta de nuevo.');
    }
  };

  /** Append a delta to the streaming placeholder, creating it on the first one. */
  const appendDelta = (text: string) => {
    setToolStatus(null);
    setMessages((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant' && last.isStreaming) {
        next[next.length - 1] = { ...last, content: last.content + text };
      } else {
        next.push({ role: 'assistant', content: text, isStreaming: true });
      }
      return next;
    });
  };

  /** Replace the streaming placeholder (or append) with the authoritative final response. */
  const finalizeResponse = (result: ChatResponse) => {
    setToolStatus(null);
    setMessages((prev) => {
      // The server keeps ONE staged action per user: a new proposal silently
      // discards the previous one, so retire any older card still showing buttons.
      let next: ChatMessage[] = result.pendingAction
        ? prev.map((msg): ChatMessage =>
            msg.pendingActionStatus === 'pending'
              ? { ...msg, pendingActionStatus: 'expired' }
              : msg
          )
        : [...prev];
      const last = next[next.length - 1];
      if (last?.role === 'assistant' && last.isStreaming) {
        next = next.slice(0, -1);
      }
      return [
        ...next,
        {
          role: 'assistant',
          content: result.response,
          pendingAction: result.pendingAction ?? undefined,
          pendingActionStatus: result.pendingAction ? 'pending' : undefined,
        },
      ];
    });
  };

  /** Drop the streaming flag but keep any partial text already shown. */
  const settlePartialStream = () => {
    setMessages((prev) =>
      prev.map((msg) => (msg.isStreaming ? { ...msg, isStreaming: undefined } : msg))
    );
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending || !currentRestaurant) return;

    const userMessage: ChatMessage = { role: 'user', content: trimmed };
    // Strip card/stream metadata — the backend only consumes role/content.
    const history = messages.slice(-10).map(({ role, content }) => ({ role, content }));
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setError(null);
    setSending(true);
    setToolStatus(null);

    const requestPayload = { message: trimmed, history };
    let receivedDelta = false;

    try {
      if (!accessToken) throw new Error('stream-unavailable:no-token');
      const result = await streamChat({
        restaurantId: currentRestaurant.id,
        request: requestPayload,
        accessToken,
        onDelta: (text) => {
          receivedDelta = true;
          appendDelta(text);
        },
        onTool: () => setToolStatus('Consultando datos del restaurante…'),
      });
      finalizeResponse(result);
    } catch (streamErr: any) {
      if (receivedDelta) {
        // Mid-stream failure: keep the partial text, surface the error.
        settlePartialStream();
        setError(streamErr?.message || 'Se interrumpio la respuesta. Intenta de nuevo.');
      } else {
        // Streaming never started (proxy without SSE, red flaky, token) —
        // fall back to the blocking endpoint so the chat keeps working.
        try {
          const result = await askChat({
            restaurantId: currentRestaurant.id,
            request: requestPayload,
          }).unwrap();
          finalizeResponse(result);
        } catch (err: any) {
          const errorMessage =
            err?.data?.message ||
            err?.data?.details ||
            'Error al procesar tu consulta. Intenta de nuevo.';
          setError(errorMessage);
        }
      }
    } finally {
      setSending(false);
      setToolStatus(null);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <div className="ai-chat-messages">
        {messages.length === 0 && (
          <div className="ai-chat-welcome">
            <div className="ai-chat-welcome-icon">
              <RobotOutlined />
            </div>
            <h4>Asistente IA</h4>
            <p>
              Pregunta sobre ventas, platillos populares, desempe&ntilde;o de empleados,
              tendencias y m&aacute;s. Tambi&eacute;n puedo ayudarte a gestionar tu men&uacute;.
            </p>
            <p className="ai-chat-beta-note">
              Esta funci&oacute;n est&aacute; en versi&oacute;n Beta. Las respuestas pueden no ser 100% precisas.
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <AiChatMessage
            key={idx}
            message={msg}
            onConfirmAction={handleConfirmAction}
            onCancelAction={handleCancelAction}
            actionBusy={actionBusy}
          />
        ))}

        {sending && !isStreamingVisible && (
          <div className="ai-chat-typing">
            <div className="ai-chat-typing-dot" />
            <div className="ai-chat-typing-dot" />
            <div className="ai-chat-typing-dot" />
            {toolStatus && <span className="ai-chat-tool-status">{toolStatus}</span>}
          </div>
        )}

        {error && <div className="ai-chat-error">{error}</div>}

        <div ref={messagesEndRef} />
      </div>

      <div className="ai-chat-input-area">
        {messages.length > 0 && (
          <Button
            type="text"
            icon={<DeleteOutlined />}
            onClick={handleClearChat}
            title="Limpiar chat"
            size="small"
            className="ai-chat-clear-btn"
          />
        )}
        <Input.TextArea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe tu pregunta..."
          autoSize={{ minRows: 1, maxRows: 3 }}
          disabled={sending}
        />
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={handleSend}
          loading={sending}
          disabled={!input.trim()}
          className="ai-chat-send-btn"
        />
      </div>
    </>
  );
};

export default AiChatPanel;
